import { type AH_ToolQuestionPayload, NUMEO_CLIENT_HEADER, NumeoClient } from '@numeo/types'
import { loadNumberOf, PhotoType } from '@numeo/types-core'
import { ongoingLoadsApi } from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/api/ongoing-loads.api'
import { generalAgentServiceAxios } from 'setup/SetupAxios'
import type { GeneralChatMessage, GeneralChatSummary } from '../types'

// The header selects the general agent (ADR-048) on general-agent-service, for the tab and for each turn.
const GENERAL_CLIENT_HEADERS = { [NUMEO_CLIENT_HEADER]: NumeoClient.General }
// The bubbles a chat shows. Tool rows stay out of the page, so the newest replies are never crowded past it.
const SHOWN_TYPES = 'user,agent-response'
// How many of the newest bubbles a chat opens with.
const PAGE_LIMIT = 100

/** Opens a new general-agent chat tab, titled by its first message, and returns its id. */
export async function createGeneralChat(title: string): Promise<string> {
    const response = await generalAgentServiceAxios.post<{ success: boolean; data: { id: string } }>('/tabs', { title }, { headers: GENERAL_CLIENT_HEADERS })
    return response.data.data.id
}

/** The user's general-agent chats, newest activity first. */
export async function listGeneralChats(): Promise<GeneralChatSummary[]> {
    const response = await generalAgentServiceAxios.get<{ success: boolean; data: GeneralChatSummary[] }>('/tabs', {
        params: { page: 1, limit: 50 },
        headers: GENERAL_CLIENT_HEADERS,
    })
    return response.data.data
}

/** Sends one user message. The server answers 202 and runs the turn in the background. The browser's zone tells the
 *  agent which day "today" is for the user. */
export async function sendGeneralMessage(tabId: string, message: string): Promise<void> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    await generalAgentServiceAxios.post(`/tabs/${tabId}/messages`, { message }, { headers: { ...GENERAL_CLIENT_HEADERS, 'x-numeo-time-zone': timeZone } })
}

/** The user's answer to a card's question. The waiting turn resumes in the background (`resumed`); an unknown or
 *  already answered question is a 404, a late answer a 410, and nothing is done for either. */
export async function answerToolQuestion(tabId: string, questionId: string, action: 'accept' | 'decline'): Promise<string> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const response = await generalAgentServiceAxios.post<{ success: boolean; data: { status: string } }>(
        `/tabs/${tabId}/questions/${questionId}/answer`,
        { action },
        { headers: { ...GENERAL_CLIENT_HEADERS, 'x-numeo-time-zone': timeZone } }
    )
    return response.data.data.status
}

/** The card question the tab waits on, for a page opened while the turn waits, or null. */
export async function fetchPendingToolQuestion(tabId: string): Promise<AH_ToolQuestionPayload | null> {
    const response = await generalAgentServiceAxios.get<{ success: boolean; data: { question: AH_ToolQuestionPayload | null } }>(
        `/tabs/${tabId}/questions/pending`,
        { headers: GENERAL_CLIENT_HEADERS }
    )
    return response.data.data.question ?? null
}

/** The tab's newest bubbles (user messages and replies), oldest first. */
export async function fetchGeneralMessages(tabId: string): Promise<GeneralChatMessage[]> {
    const response = await generalAgentServiceAxios.get<{ success: boolean; data: { items: GeneralChatMessage[] } }>(`/tabs/${tabId}/messages`, {
        params: { page: 1, limit: PAGE_LIMIT, sort: 'desc', surface: 'chat', types: SHOWN_TYPES },
        headers: GENERAL_CLIENT_HEADERS,
    })
    return [...response.data.data.items].reverse()
}

/** The tab's newest card answers (`AgentCardResult` in `tool_output`), oldest first, over the same window as its bubbles.
 *  The server keeps only those rows (`cardResultsOnly`), so other tools' output never comes down. */
export async function fetchGeneralToolResults(tabId: string): Promise<GeneralChatMessage[]> {
    const response = await generalAgentServiceAxios.get<{ success: boolean; data: { items: GeneralChatMessage[] } }>(`/tabs/${tabId}/messages`, {
        params: { page: 1, limit: PAGE_LIMIT, sort: 'desc', surface: 'chat', types: 'agent-tool-result', cardResultsOnly: 'true' },
        headers: GENERAL_CLIENT_HEADERS,
    })
    return [...response.data.data.items].reverse()
}

// The board's tabs a load can be on. History first: it is where a load awaiting its POD sits, and the only tab whose
// list does no upkeep (the other two archive old loads and clean up enrichments on every read).
const BOARD_STATUSES = ['history', 'in-transit', 'upcoming'] as const
// Rows per page of the board's search; the search prefix-matches a short number, so the exact one may be on a later page.
const BOARD_SEARCH_LIMIT = 50

/**
 * The ids of the board's loads showing this number, through the board's own list search (the call Ongoing Loads makes):
 * tab by tab, page by page, stopping at the first page with an exact match. Empty when no load shows it.
 */
export async function findBoardLoadIds(loadNumber: string): Promise<string[]> {
    for (const status of BOARD_STATUSES) {
        for (let page = 1; ; page++) {
            const result = await ongoingLoadsApi.getOngoingLoads(status, page, BOARD_SEARCH_LIMIT, undefined, { search: loadNumber })
            const ids = [...new Set(result.data.filter((load) => loadNumberOf(load) === loadNumber).map((load) => load.id))]
            if (ids.length) return ids
            if (page >= (result.pagination?.pages ?? 1) || result.data.length < BOARD_SEARCH_LIMIT) break
        }
    }
    return []
}

/** Files one document on a load under `type`, through the board's own upload (`/updater/loads/doc/merge-upload`). A rate con's
 *  revision is reviewed against the broker-facing rate, as invoicing is; the board's own upload keeps its baseline. */
export const uploadBoardDocument = (loadId: string, file: File, type: PhotoType) =>
    type === PhotoType.RATECON
        ? ongoingLoadsApi.mergeUploadDocuments(loadId, [file], type, [0], { revisionBaseline: 'originalRate' })
        : ongoingLoadsApi.mergeUploadDocuments(loadId, [file], type, [0])

/** Creates a load from a rate con, through the board's own upload (`/updater/loads/process`). A con for a load already
 *  on the board opens that load's revision, as the board's merge-upload does, instead of updating the load. */
export const createBoardLoadFromRatecon = (file: File) => ongoingLoadsApi.processFile(file, undefined, { ifLoadExists: 'revision' })
